import type { OpenArenaVoteWithConfigNames } from '@meridian-ssa/shared';
import { useEffect, useMemo, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import { AddToSelectionButton } from '../SelectionPanel';
import { SpreadJSViewer } from '../SpreadJSViewer';

interface ArenaResult {
  id: string;
  model_config_id: string;
  config_name?: string;
  status: string;
  is_valid_json: boolean;
  evaluation_ok: boolean;
  formula_errors: boolean;
  execution_time_ms: number;
  token_usage?: any;
  engine_checks?: any;
  error?: string;
  sheet_json?: any;
  compute_values?: any;
}

/** Minimal run context needed by the modal */
export interface VoteRunContext {
  prompt_text: string;
  prompt_id: string | null;
  category: string | null;
  source: 'seed' | 'arena' | null;
}

export function VoteComparisonModal({
  vote,
  run,
  onClose,
}: {
  vote: OpenArenaVoteWithConfigNames;
  run: VoteRunContext | null;
  onClose: () => void;
}) {
  const [resultA, setResultA] = useState<ArenaResult | null>(null);
  const [resultB, setResultB] = useState<ArenaResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchResults() {
      try {
        setLoading(true);
        const [resA, resB] = await Promise.all([
          apiRequest(`/api/arena/results/${vote.result_a_id}`),
          apiRequest(`/api/arena/results/${vote.result_b_id}`),
        ]);
        setResultA(resA.result);
        setResultB(resB.result);
      } catch (err) {
        logError('Failed to fetch vote results', err);
      } finally {
        setLoading(false);
      }
    }
    fetchResults();
  }, [vote.result_a_id, vote.result_b_id]);

  const isAWinner = vote.choice === 'a';
  const isBWinner = vote.choice === 'b';

  const selectionData = useMemo(() => {
    return {
      id: vote.id,
      arenaRunId: vote.arena_run_id,
      promptText: run?.prompt_text || '',
      promptId: run?.prompt_id || null,
      category: run?.category || null,
      source: run?.source || null,
      resultA: {
        id: vote.result_a_id,
        configId: vote.config_a_id,
        configName: vote.config_a_name,
        sheetJson: resultA?.sheet_json || null,
      },
      resultB: {
        id: vote.result_b_id,
        configId: vote.config_b_id,
        configName: vote.config_b_name,
        sheetJson: resultB?.sheet_json || null,
      },
      winner: vote.choice,
    };
  }, [vote, run, resultA, resultB]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '2rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '1400px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid #E8E0D8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#1A1A1A',
                margin: 0,
                marginBottom: '0.5rem',
              }}
            >
              Vote Comparison
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6B6B6B', margin: 0 }}>
              {vote.choice === 'tie'
                ? 'Result: Tie'
                : vote.choice === 'both_bad'
                  ? 'Result: Both Bad'
                  : `Winner: ${isAWinner ? vote.config_a_name : vote.config_b_name}`}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <AddToSelectionButton battle={selectionData} />
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6B6B6B',
                padding: '0.5rem',
              }}
            >
              ×
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {loading ? (
            <div
              style={{ textAlign: 'center', padding: '4rem', color: '#6B6B6B' }}
            >
              Loading spreadsheets...
            </div>
          ) : !resultA && !resultB ? (
            <div
              style={{ textAlign: 'center', padding: '4rem', color: '#C41E3A' }}
            >
              Failed to load results. Please try again.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1.5rem',
              }}
            >
              <div
                style={{
                  border: `2px solid ${isAWinner ? '#2E7D4A' : '#E8E0D8'}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    padding: '1rem',
                    background: isAWinner ? '#ECFDF5' : '#F5F0EB',
                    borderBottom: '1px solid #E8E0D8',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      color: isAWinner ? '#2E7D4A' : '#1A1A1A',
                    }}
                  >
                    {vote.config_a_name}
                  </span>
                  {isAWinner && (
                    <span
                      style={{
                        background: '#2E7D4A',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      WINNER
                    </span>
                  )}
                  {vote.choice === 'b' && (
                    <span
                      style={{
                        background: '#C41E3A',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      LOSER
                    </span>
                  )}
                </div>
                <div
                  style={{
                    padding: '1rem',
                    border: '1px solid #E8E0D8',
                    borderRadius: '8px',
                    margin: '0.5rem',
                  }}
                >
                  {resultA?.sheet_json?.sheets?.length > 0 ? (
                    <SpreadJSViewer
                      sheetJson={resultA!.sheet_json}
                      computedValues={resultA!.compute_values}
                      height={400}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: '#6B6B6B',
                        height: '400px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#FEF2F2',
                        borderRadius: '8px',
                      }}
                    >
                      <p style={{ margin: 0 }}>No spreadsheet data available</p>
                    </div>
                  )}
                </div>
              </div>
              <div
                style={{
                  border: `2px solid ${isBWinner ? '#2E7D4A' : '#E8E0D8'}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    padding: '1rem',
                    background: isBWinner ? '#ECFDF5' : '#F5F0EB',
                    borderBottom: '1px solid #E8E0D8',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      color: isBWinner ? '#2E7D4A' : '#1A1A1A',
                    }}
                  >
                    {vote.config_b_name}
                  </span>
                  {isBWinner && (
                    <span
                      style={{
                        background: '#2E7D4A',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      WINNER
                    </span>
                  )}
                  {vote.choice === 'a' && (
                    <span
                      style={{
                        background: '#C41E3A',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      LOSER
                    </span>
                  )}
                </div>
                <div
                  style={{
                    padding: '1rem',
                    border: '1px solid #E8E0D8',
                    borderRadius: '8px',
                    margin: '0.5rem',
                  }}
                >
                  {resultB?.sheet_json?.sheets?.length > 0 ? (
                    <SpreadJSViewer
                      sheetJson={resultB!.sheet_json}
                      computedValues={resultB!.compute_values}
                      height={400}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: '#6B6B6B',
                        height: '400px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#FEF2F2',
                        borderRadius: '8px',
                      }}
                    >
                      <p style={{ margin: 0 }}>No spreadsheet data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
